import { ErrorBoundary } from 'components/common/error-boundary';
import { SessionHitsHeaderNew } from 'components/sections/analytics/session-hits/header-new';
import { SessionRecap } from 'components/sections/analytics/session-hits/recap';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { IVerboseComponent } from 'interfaces/i-components';
import {
  LOCAL_PRETTY_DATETIME_FORMAT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { paths } from 'routes/paths';
import useHittersStore from 'stores/use-hitters-store';

export const HPHitterSessionNew = (props: IVerboseComponent) => {
  const navigate = useNavigate();
  const sessions = useAnalyticsStore((s) => s.sessions);

  const hitters = useHittersStore((s) => s.hitters);

  const { hitterID, session: sessionID } = useParams<{
    hitterID: string;
    session: string;
  }>();

  const session = useMemo(
    () => sessions.find((s) => s.session === sessionID),
    [sessionID, sessions]
  );

  const hitter = useMemo(
    () => hitters.find((h) => h._id === hitterID),
    [hitterID, hitters]
  );

  // e.g. linked directly to a hitter report without going through the sessions table first
  useEffect(() => {
    if (!sessionID) {
      return;
    }

    // for Hitter Profiles, we'll always have a hitter ID
    if (!hitterID) {
      return;
    }

    useAnalyticsStore.getState().fetchSessions({
      sessionID,
      hitterID,
      limit: 1,
    });

    return () => {
      useAnalyticsStore.getState().reset();
    };
  }, [sessionID, hitterID]);

  if (!hitterID || !sessionID) {
    return null;
  }

  return (
    <ErrorBoundary identifier={props.identifier}>
      <SessionRecap
        sessionID={sessionID}
        hitterID={hitterID}
        machineID={session?.machineID}
        // Breadcrumbs and summary rendered in HitterSesionHeader
        header={
          <SessionHitsHeaderNew
            sessionID={sessionID}
            hitterID={hitterID}
            breadcrumbs={[
              {
                label: 'main.hitters',
                onClick: () => navigate(paths.hitterLibrary.hitters),
              },
              {
                label: hitter?.name ?? 'Hitter',
                onClick: () => navigate(paths.hitterLibrary.profile(hitterID)),
              },
              ...(session
                ? [
                    {
                      label: format(
                        parseISO(session.startDate),
                        LOCAL_PRETTY_DATETIME_FORMAT,
                        { timeZone: LOCAL_TIMEZONE }
                      ),
                    },
                  ]
                : []),
            ]}
            onDeleteSession={() =>
              navigate(paths.hitterLibrary.profile(hitterID))
            }
          />
        }
      />
    </ErrorBoundary>
  );
};
